import {useEffect, useRef, useState, type ChangeEvent, type MouseEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Loader2,
  Maximize,
  Minimize,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import {Button} from '../button';
import {useClickEscape} from '../hooks';

type VideoSource = 'file' | 'youtube' | 'vimeo';

export interface VideoPlayerProps {
  src: string;
  poster?: string;
  title?: string;
  onClose?: () => void;
  isFullScreen?: boolean;
  autoPlay?: boolean;
}

export function VideoPlayer({
  src,
  poster,
  title,
  onClose,
  isFullScreen = false,
  autoPlay = false,
}: VideoPlayerProps) {
  const {t} = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullScreenMode, setIsFullScreenMode] = useState(isFullScreen);
  const [isLoading, setIsLoading] = useState(true);
  const [videoSource, setVideoSource] = useState<VideoSource>('file');
  const [youtubeEmbedUrl, setYoutubeEmbedUrl] = useState('');
  const [vimeoEmbedUrl, setVimeoEmbedUrl] = useState('');

  useClickEscape({enabled: isFullScreenMode, onClick: onClose});

  useEffect(() => {
    const youtubeRegex =
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
    const vimeoRegex = /(?:vimeo\.com\/|vimeo\.com\/video\/)(\d+)/;

    const youtubeMatch = src.match(youtubeRegex);
    const vimeoMatch = src.match(vimeoRegex);

    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      setVideoSource('youtube');
      setYoutubeEmbedUrl(
        `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1${autoPlay ? '&autoplay=1' : ''}`,
      );
      setVimeoEmbedUrl('');
      setIsLoading(false);
      return;
    }

    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      setVideoSource('vimeo');
      setVimeoEmbedUrl(
        `https://player.vimeo.com/video/${videoId}?h=0&title=0&byline=0&portrait=0${autoPlay ? '&autoplay=1' : ''}`,
      );
      setYoutubeEmbedUrl('');
      setIsLoading(false);
      return;
    }

    setVideoSource('file');
    setYoutubeEmbedUrl('');
    setVimeoEmbedUrl('');
  }, [src, autoPlay]);

  useEffect(() => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    const updateDuration = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };
    const updateTime = () => setCurrentTime(video.currentTime);
    const handleVideoEnd = () => {
      setIsPlaying(false);
      video.currentTime = 0;
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('ended', handleVideoEnd);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('ended', handleVideoEnd);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoSource]);

  useEffect(() => {
    if (isFullScreenMode && videoRef.current && videoSource === 'file') {
      void videoRef.current.play();
      setIsPlaying(true);
    }
  }, [isFullScreenMode, videoSource]);

  const togglePlay = () => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      void video.play();
    }

    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    const newTime = Number.parseFloat(event.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullScreen = () => {
    if (!isFullScreen && !isFullScreenMode) {
      setIsFullScreenMode(true);
      return;
    }

    setIsFullScreenMode(false);
    onClose?.();
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const skipBackward = () => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.max(0, video.currentTime - 5);
  };

  const skipForward = () => {
    if (videoSource !== 'file') return;

    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.min(video.duration, video.currentTime + 5);
  };

  const containerClasses = isFullScreenMode
    ? 'fixed inset-0 z-[300] flex cursor-pointer items-center justify-center bg-gradient-to-br from-black/95 via-black/90 to-black/95 backdrop-blur-sm animate-in fade-in-0 duration-500'
    : 'relative h-full w-full';

  const videoContainerClasses = isFullScreenMode
    ? 'w-full max-w-5xl cursor-default animate-in zoom-in-95 fade-in-0 duration-700 ease-out'
    : 'h-full w-full';

  const handleBackgroundClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      onClick={isFullScreenMode ? handleBackgroundClick : undefined}
    >
      {isFullScreenMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/4 top-1/4 h-2 w-2 animate-pulse rounded-full bg-white/20 duration-1000" />
          <div className="absolute left-1/3 top-3/4 h-1 w-1 animate-pulse rounded-full bg-white/30 delay-500 duration-1000" />
          <div className="absolute right-1/4 top-1/2 h-1.5 w-1.5 animate-pulse rounded-full bg-white/20 delay-1000 duration-1000" />
          <div className="absolute bottom-1/4 right-1/3 h-1 w-1 animate-pulse rounded-full bg-white/25 delay-1500 duration-1000" />
          <div className="absolute right-1/6 top-1/6 h-2 w-2 animate-pulse rounded-full bg-white/15 delay-2000 duration-1000" />
        </div>
      )}

      {isFullScreenMode && onClose && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full text-white transition-all duration-300 hover:scale-110 hover:bg-white/20 active:scale-95"
          aria-label={t('close')}
        >
          <X className="h-6 w-6" />
        </Button>
      )}

      {title && isFullScreenMode && (
        <div className="absolute left-4 top-4 z-10 animate-in slide-in-from-left-4 text-white delay-200 duration-500">
          <h3 className="text-lg font-medium drop-shadow-lg">{title}</h3>
        </div>
      )}

      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
          <div className="flex items-center gap-3 text-white">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-lg font-medium">{t('loading_video')}</span>
          </div>
        </div>
      )}

      <div className={videoContainerClasses} onClick={event => event.stopPropagation()}>
        {videoSource === 'youtube' ? (
          <iframe
            ref={iframeRef}
            src={youtubeEmbedUrl}
            className={`w-full ${isFullScreenMode ? 'h-[70vh]' : 'h-full'} rounded-lg bg-black shadow-2xl`}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            onLoad={() => setIsLoading(false)}
            title={title || 'YouTube video player'}
          />
        ) : videoSource === 'vimeo' ? (
          <iframe
            ref={iframeRef}
            src={vimeoEmbedUrl}
            className={`w-full ${isFullScreenMode ? 'h-[70vh]' : 'h-full'} rounded-lg bg-black shadow-2xl`}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            onLoad={() => setIsLoading(false)}
            title={title || 'Vimeo video player'}
          />
        ) : (
          <>
            <video
              ref={videoRef}
              className={`w-full ${isFullScreenMode ? 'max-h-[70vh]' : 'h-full'} rounded-lg bg-black shadow-2xl`}
              poster={poster}
              onClick={togglePlay}
              playsInline
              autoPlay={autoPlay}
            >
              <source src={src} type="video/mp4" />
              {t('video_not_supported')}
            </video>

            <div
              className={`absolute bottom-0 left-0 right-0 rounded-b-lg bg-gradient-to-t from-black/80 via-black/60 to-transparent p-3 transition-all duration-300 hover:from-black/90 hover:via-black/70 ${isFullScreenMode ? 'pb-6' : ''}`}
            >
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-white/30 transition-all duration-200 hover:bg-white/40 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-200 [&::-webkit-slider-thumb]:hover:scale-125"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={togglePlay}
                    className="h-10 w-10 rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95"
                    aria-label={isPlaying ? t('pause') : t('play')}
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={skipBackward}
                    className="h-10 w-10 rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95"
                    aria-label={t('skip_backward')}
                  >
                    <SkipBack className="h-5 w-5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={skipForward}
                    className="h-10 w-10 rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95"
                    aria-label={t('skip_forward')}
                  >
                    <SkipForward className="h-5 w-5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleMute}
                    className="h-10 w-10 rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95"
                    aria-label={isMuted ? t('unmute') : t('mute')}
                  >
                    {isMuted ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </Button>

                  <span className="text-sm font-medium text-white/90">
                    {formatTime(currentTime)} / {formatTime(duration || 0)}
                  </span>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleFullScreen}
                  className="h-10 w-10 rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-white/20 active:scale-95"
                  aria-label={
                    isFullScreenMode ? t('exit_full_screen') : t('enter_full_screen')
                  }
                >
                  {isFullScreenMode ? (
                    <Minimize className="h-5 w-5" />
                  ) : (
                    <Maximize className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
